const safeFilename = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "puckly-plan";

const waitForImages = (element: HTMLElement) =>
  Promise.all(
    [...element.querySelectorAll("img")].map(
      (image) =>
        image.complete ||
        new Promise<void>((resolve) => {
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  );

export async function createPdfFile(
  source: HTMLElement,
  title: string,
  kind: "drill" | "practice",
) {
  const clone = source.cloneNode(true) as HTMLElement;
  clone.classList.add("pdf-capture-clone", `pdf-${kind}-capture`);
  clone.setAttribute("aria-hidden", "true");
  document.body.append(clone);
  try {
    await document.fonts?.ready;
    await waitForImages(clone);
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const canvas = await html2canvas(clone, {
      scale: 1.6,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const margin = 8;
    const availableWidth = 210 - margin * 2;
    const availableHeight = 297 - margin * 2;
    const scale = Math.min(
      availableWidth / canvas.width,
      availableHeight / canvas.height,
    );
    const width = canvas.width * scale;
    const height = canvas.height * scale;
    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.94),
      "JPEG",
      margin + (availableWidth - width) / 2,
      margin,
      width,
      height,
      undefined,
      "FAST",
    );
    const blob = pdf.output("blob");
    return new File([blob], `${safeFilename(title)}.pdf`, {
      type: "application/pdf",
    });
  } finally {
    clone.remove();
  }
}

export async function sharePdfFile(file: File, title: string) {
  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title });
    return "shared" as const;
  }
  downloadPdfFile(file);
  return "downloaded" as const;
}

export function downloadPdfFile(file: File) {
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

